// The onboarding packet (PORTAL-PACKET-2026-10-09 §1, §4, §6): the candidate's answers filled onto Matt's own six PDFs
// (public/forms/, blank), then the uploads. Built in the staff member's browser, never stored: the decrypted values come
// from onboarding_adp_view (each call logged, as "Ready for ADP"), the uploads through 60-second signed links.
//
// Matt's PDFs have no form fields, so every value is a text overlay at the form's own spot. Positions are in PDF points
// measured from the TOP-LEFT of the page (y is the text baseline) -- converted to pdf-lib's bottom-left origin when drawn.
// A value the candidate has not given is left blank: never inferred, never invented.
//
// Every function here is pure except render*/buildPacket, which take pdf-lib (loaded on demand) and fetchers as arguments.

import { EXAM_BY_ID } from './exams';
export const FORMS = [
  { key: 'background', file: 'background-consent.pdf', label: 'Background Consent' },
  { key: 'direct_deposit', file: 'direct-deposit.pdf', label: 'Direct Deposit' },
  { key: 'emergency', file: 'emergency-contact.pdf', label: 'Emergency Contact' },
  { key: 'i9', file: 'i9.pdf', label: 'I-9' },
  { key: 'tax_home', file: 'tax-home-questionnaire.pdf', label: 'Tax Home Questionnaire' },
  { key: 'w4', file: 'w4-2026.pdf', label: 'W-4' },
];
// The uploads that follow the forms, in this order (§6).
export const PACKET_UPLOADS = ['voided_check', 'gov_id_front', 'gov_id_back', 'i20'];
// §3: the ID photos sit right after the I-9, beside its Section 2, so staff can complete it from them.
export const BESIDE_I9 = ['gov_id_front', 'gov_id_back'];

const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const yes = (v) => v === true || v === 'true' || v === 'yes';

/** "First Middle Last" -> { first, middle, last }. One word -> last only is wrong; it goes to first. */
export function nameParts(full) {
  const w = s(full).split(/\s+/).filter(Boolean);
  if (w.length === 0) return { first: '', middle: '', last: '' };
  if (w.length === 1) return { first: w[0], middle: '', last: '' };
  return { first: w[0], middle: w.length > 2 ? w.slice(1, -1).join(' ') : '', last: w[w.length - 1] };
}
/** ISO date or timestamp -> mm/dd/yyyy (UTC for timestamps); '' for nothing. */
export function mdy(v) {
  const t = s(v);
  if (!t) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}/${d.getUTCFullYear()}`;
}
export const utc = (v) => (s(v) ? new Date(v).toISOString().replace('T', ' ').replace(/:\d{2}\.\d{3}Z$/, ' UTC') : '');
export function ssnFmt(v) {
  const d = s(v).replace(/\D/g, '');
  return d.length === 9 ? `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}` : s(v);
}
export function usd(v) {
  const t = s(v);
  if (!t) return '';
  const n = Number(t.replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : t;
}
const cityStZip = (city, st, zip) => [s(city), [s(st), s(zip)].filter(Boolean).join(' ')].filter(Boolean).join(', ');

/** The record line stamped under a signature: what the portal recorded, word for word. */
export function signatureRecord({ at, ip, consent, short = false }) {
  if (!s(at)) return '';
  // short: the same record without "in the onboarding portal", for a signature cell too small for the full line (the I-9)
  return [`Signed electronically${short ? '' : ' in the onboarding portal'} ${utc(at)}`, s(ip) ? `IP ${s(ip)}` : '', consent ? 'consent box checked' : '']
    .filter(Boolean).join(' · ');
}

/** The candidate's answers, shaped once for every form. raw = what api.packetData returns. */
export function packetModel(raw) {
  const pages = raw.pages || {};
  const data = (k) => (pages[k] && pages[k].data) || {};
  const personal = data('personal');
  const sec = raw.secrets || {};
  const legal = s(sec.legal_name) || s(personal.legal_name) || s(raw.candidate && raw.candidate.name);
  return {
    candidate: raw.candidate || {},
    legalName: legal,
    name: nameParts(legal),
    personal,
    emergency: data('emergency'),
    w4: { ...data('w4'), ...(sec.w4 || {}) },
    dd: { ...data('direct_deposit'), ...(sec.direct_deposit || {}) },
    i9: data('i9'),
    taxHome: raw.taxHome || {},
    background: raw.background || {},
    certificate: raw.certificate || null,
    sessionId: raw.sessionId ?? (raw.certificate && raw.certificate.session_id) ?? null,
    attest: Object.fromEntries(Object.entries(pages).map(([k, v]) => [k, { at: v.attested_at, ip: v.attest_ip }])),
    // PORTAL-PACKET Stage 2: each form's own signature (onboarding_form_signatures), never the generic attest box
    signatures: Object.fromEntries((raw.signatures || []).map((x) => [x.form, { name: s(x.signature), at: x.signed_at, ip: x.signed_ip,
      consent: x.consented === true }])),
    ssn: s(sec.ssn),
    routing: s(sec.routing),
    account: s(sec.account),
    routing2: s(sec.routing2), account2: s(sec.account2), routing3: s(sec.routing3), account3: s(sec.account3),
    uscisNumber: s(sec.uscis_number), i94Number: s(sec.i94_number), passportNumber: s(sec.passport_number),
  };
}

/** The signature line and its record for a form, or nothing when the form is not signed. */
function signedOps(m, form, { p = 0, x, y, w, dx, dateX, recX, recY, recW }) {
  const sg = m.signatures[form];
  if (!sg || !sg.name || !sg.at) return [];
  // §5: the caption replaces the longer record line here; the full evidence (IP, browser, hash) is on the certificate
  return [sig(p, x, y, sg.name, w), t(p, dateX, y + (dx || 0), mdy(sg.at), 120, 10),
    rec(p, recX ?? x, recY, signatureCaption(sg.at, m.sessionId), recW)];
}

// ---------------------------------------------------------------- the six forms: what goes where
// op kinds: { p, x, y, text, size?, maxW?, font? }  text at baseline (x, y) shrunk to fit maxW
//           { p, x, y, mark: true }                 an "X" centred on (x, y)
//           { p, cx, cy, rx, ry, circle: true }      an ellipse around a printed choice
//           { p, x, y, w, h, white: true }            a white box over printed text
const t = (p, x, y, text, maxW, size = 9, font) => ({ p, x, y, text: s(text), maxW, size, font });
/** Words into at most `lines` lines of about `width` characters; the last line keeps whatever is left. */
export function wrapWords(text, width, lines) {
  const out = [];
  let cur = '';
  for (const w of s(text).split(/\s+/).filter(Boolean)) {
    if (out.length === lines - 1) { cur = cur ? `${cur} ${w}` : w; continue; }
    if (cur && `${cur} ${w}`.length > width) { out.push(cur); cur = w; } else cur = cur ? `${cur} ${w}` : w;
  }
  out.push(cur);
  while (out.length < lines) out.push('');
  return out;
}
// PORTAL-ESIGN-PROOF-2026-10-12 §5: a typed signature is drawn in Dancing Script (OFL, embedded as a subset) -- or
// Helvetica Oblique when the font is not available. What is stored and hashed stays the typed text.
const sig = (p, x, y, text, maxW) => t(p, x, y, text, maxW, 14, 'script');
export const SCRIPT_FONT_FILE = 'DancingScript-Regular.ttf';   // public/fonts/ (with DancingScript-OFL.txt)
/** MM/DD/YYYY in Eastern time. */
export function etDate(at) {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.month}/${p.day}/${p.year}`;
}
/** The thin caption under every signature: "Electronically signed · <date ET> · ID <session id>". */
export function signatureCaption(at, sessionId) {
  return s(at) ? `Electronically signed · ${etDate(at)}${sessionId != null && sessionId !== '' ? ` · ID ${sessionId}` : ''}` : '';
}
const rec = (p, x, y, text, maxW = 520) => t(p, x, y, text, maxW, 6.5);

export function backgroundOps(m) {
  const b = m.background;
  const ops = [];
  if (b.disclosure_viewed_at) {
    // page 1, the disclosure: its acknowledgement, signed with the authorization's typed signature
    ops.push(sig(0, 104, 468, b.signature, 270), t(0, 392, 468, mdy(b.disclosure_viewed_at), 140, 11),
      t(0, 104, 516, s(b.full_name).toUpperCase(), 400, 11),
      rec(0, 104, 497, signatureCaption(b.signed_at, m.sessionId)),
      rec(0, 104, 557, b.disclosure_viewed_at ? `Disclosure viewed on its own page in the onboarding portal ${utc(b.disclosure_viewed_at)}` : ''));
  }
  // page 2, the authorization. Hub ruling (c) 2026-10-08: the form's unfilled third-party field is removed.
  ops.push({ p: 1, x: 273.4, y: 286.4, w: 192.8, h: 12.0, white: true });
  if (b.signed_at) {
    ops.push(sig(1, 32, 404, b.signature, 330), t(1, 392, 404, mdy(b.signed_at), 160, 11),
      rec(1, 32, 431, signatureCaption(b.signed_at, m.sessionId)));
  }
  // the applicant-information box
  const ad = m.personal;
  ops.push(
    t(1, 91, 519, m.name.last, 136, 8.5), t(1, 273, 519, m.name.first, 158, 8.5), t(1, 481, 519, m.name.middle.slice(0, 1), 90, 8.5),
    t(1, 75, 538.5, mdy(ad.dob), 70, 8.5), t(1, 229, 538.5, ssnFmt(m.ssn), 100, 8.5),
    t(1, 90, 558, [ad.address_line1, ad.address_line2].filter(Boolean).join(' '), 138, 8), t(1, 249, 558, ad.city, 80, 8.5),
    t(1, 355, 558, ad.state, 76, 8.5), t(1, 469, 558, ad.zip, 98, 8.5),
    t(1, 201, 616, [...new Set([s(b.aliases), s(ad.other_names)].filter(Boolean))].join('; '), 366, 8.5),
  );
  // PORTAL-DEGREE-2026-10-12: the form's own education row -- Degree Obtained, Year Graduated, Name of School, City and
  // State of School -- and Last Name Used at Time of Graduation. Year Graduated is a narrow cell: the year alone there;
  // the month and the major are on the degree-details page that follows the form.
  if (s(b.degree)) {
    ops.push(t(1, 87, 636, b.degree, 93, 8.5), t(1, 237, 636, s(b.degree_graduated).slice(0, 4), 20, 8.5),
      t(1, 314, 636, b.degree_school, 117, 8.5), t(1, 516, 636, b.degree_school_city_state, 54, 8.5),
      t(1, 160, 655, b.degree_last_name, 405, 8.5));
  }
  // previous addresses (past 7 years), when the portal has them (Stage 2)
  for (const [i, y] of [[1, 577.5], [2, 597]]) {
    ops.push(t(1, 143, y, [ad[`prev${i}_address`]].filter(Boolean).join(' '), 86, 8), t(1, 249, y, ad[`prev${i}_city`], 80, 8.5),
      t(1, 355, y, ad[`prev${i}_state`], 76, 8.5), t(1, 469, y, ad[`prev${i}_zip`], 98, 8.5));
  }
  return ops;
}

// Direct Deposit: a scanned page. Each account block repeats 151.9 pt lower.
export function directDepositOps(m) {
  const dd = m.dd;
  const ops = [];
  const acct = (n) => (n === 1 ? { type: dd.account_type, bank: dd.bank_name, city: dd.bank_city, state: dd.bank_state, zip: dd.bank_zip,
    routing: m.routing, account: m.account, split: dd.split_type, amount: dd.split_amount }
    : { type: dd[`a${n}_account_type`], bank: dd[`a${n}_bank_name`], city: dd[`a${n}_bank_city`], state: dd[`a${n}_bank_state`],
      zip: dd[`a${n}_bank_zip`], routing: m[`routing${n}`], account: m[`account${n}`], split: dd[`a${n}_split_type`],
      amount: dd[`a${n}_split_amount`] });
  for (const n of [1, 2, 3]) {
    const a = acct(n);
    const dy = (n - 1) * 151.9;
    if (a.type === 'checking') ops.push({ p: 0, cx: 217.4, cy: 157 + dy, rx: 30, ry: 9, circle: true });
    if (a.type === 'savings') ops.push({ p: 0, cx: 361.4, cy: 157 + dy, rx: 27, ry: 9, circle: true });
    ops.push(t(0, 166, 186 + dy, a.bank, 340, 10), t(0, 118, 212 + dy, a.city, 104, 10), t(0, 280, 212 + dy, a.state, 46, 10),
      t(0, 440, 212 + dy, a.zip, 66, 10), t(0, 210, 238 + dy, a.routing, 190, 10), t(0, 180, 263.5 + dy, a.account, 220, 10));
    if (a.split === 'net') ops.push({ p: 0, x: 461, y: 259.5 + dy, mark: true });
    if (a.split === 'amount' || a.split === 'percent') {
      ops.push({ p: 0, x: 461, y: 234 + dy, mark: true }, t(0, 544, 238 + dy, a.split === 'percent' ? `${s(a.amount)}%` : usd(a.amount), 34, 9));
    }
  }
  const ad = m.personal;
  ops.push(t(0, 80, 674, m.legalName, 180, 10),
    t(0, 316, 674, [[ad.address_line1, ad.address_line2].filter(Boolean).join(' '), cityStZip(ad.city, ad.state, ad.zip)].filter(Boolean).join(', '), 190, 8));
  ops.push(...signedOps(m, 'direct_deposit', { x: 272, y: 709.5, w: 230, dateX: 80, recX: 36, recY: 738 }));
  return ops;
}

export function emergencyOps(m) {
  const e = m.emergency;
  const ad = m.personal;
  const phone = (v, tag) => (s(v) ? `${s(v)}${tag ? ` (${tag})` : ''}` : '');
  const ops = [
    t(0, 79, 244, m.legalName, 220), t(0, 310, 244, mdy(ad.dob), 200),
    t(0, 79, 302, s(ad.home_phone) || phone(ad.mobile, 'mobile'), 105), t(0, 192, 302, ad.work_phone, 105),
    t(0, 79, 331, [ad.address_line1, ad.address_line2].filter(Boolean).join(' '), 220), t(0, 79, 360, cityStZip(ad.city, ad.state, ad.zip), 220),
  ];
  for (const [n, x0, x1] of [[1, 79, 192], [2, 310, 424]]) {
    const name = s(e[`c${n}_name`]);
    const rel = s(e[`c${n}_relationship`]);
    ops.push(t(0, x0, 433, name ? `${name}${rel ? ` (${rel})` : ''}` : '', 220),
      t(0, x0, 462, s(e[`c${n}_home_phone`]) || phone(e[`c${n}_mobile`], 'mobile'), 105),
      t(0, x1, 462, s(e[`c${n}_work_phone`]) || phone(e[`c${n}_alt_phone`], 'other'), 105),
      t(0, x0, 491, e[`c${n}_address`], 220), t(0, x0, 520, cityStZip(e[`c${n}_city`], e[`c${n}_state`], e[`c${n}_zip`]), 220));
  }
  const allergies = s(e.allergies);
  const [l1, l2] = wrapWords(allergies, 100, 2);
  if (allergies) ops.push(t(0, 79, 588, l1, 470, 9), t(0, 79, 600, l2, 470, 9));
  ops.push(...signedOps(m, 'emergency', { x: 79, y: 660, w: 280, dateX: 372, recY: 690 }));
  return ops;
}

export function i9Ops(m) {
  const ad = m.personal;
  const i = m.i9;
  const ops = [
    t(0, 44, 184, s(i.last_name) || m.name.last, 155), t(0, 206, 184, s(i.first_name) || m.name.first, 138),
    t(0, 350, 184, s(i.middle_initial) || m.name.middle.slice(0, 1), 66), t(0, 422, 184, s(i.other_last_names) || s(ad.other_names), 150),
    t(0, 44, 210, ad.address_line1, 186), t(0, 236, 210, ad.address_line2, 66), t(0, 308, 210, ad.city, 150), t(0, 464, 210, ad.state, 42),
    t(0, 512, 210, ad.zip, 60),
    // the email: as typed on About you, else the one on file (the portal prefills it, and says the I-9 takes it from there)
    t(0, 44, 236, mdy(ad.dob), 102), t(0, 152, 236, ssnFmt(m.ssn), 108), t(0, 266, 236, s(ad.email) || s(m.candidate.email), 186, 8.5),
    t(0, 458, 236, ad.mobile, 116),
  ];
  // Section 1 attestation and signature (Stage 3)
  const box = { citizen: 263.9, noncitizen_national: 275.9, permanent_resident: 287.9, authorized_alien: 299.9 }[s(i.status)];
  if (box) ops.push({ p: 0, x: 186.4, y: box + 0.1, mark: true });
  if (s(i.status) === 'permanent_resident') ops.push(t(0, 392, 290.5, m.uscisNumber, 180, 8.5));
  if (s(i.status) === 'authorized_alien') {
    ops.push(t(0, 390, 304, mdy(i.work_until), 64, 8.5), t(0, 195, 345, m.uscisNumber, 70, 8), t(0, 282, 345, m.i94Number, 100, 8),
      t(0, 402, 345, [m.passportNumber, s(i.passport_country)].filter(Boolean).join(', '), 170, 8));
  }
  // the caption ends inside the signature cell (its right border is at about 368 pt): 238 pt wide at most
  ops.push(...signedOps(m, 'i9', { x: 44, y: 368.5, w: 320, dateX: 374, recX: 120, recY: 357, recW: 238 }));
  return ops;
}

// Tax Home: Times New Roman 12 on a 12.6-pt grid; each label's baseline is its bbox bottom minus 9.
export function taxHomeOps(m) {
  const th = m.taxHome;
  const ad = m.personal;
  const yn = (v, y) => (v === 'yes' || v === true ? [{ p: 0, cx: 136, cy: y, rx: 15, ry: 9, circle: true }]
    : v === 'no' || v === false ? [{ p: 0, cx: 206.1, cy: y, rx: 13, ry: 9, circle: true }] : []);
  const perm = { a1: s(th.perm_address_line1), a2: s(th.perm_address_line2), city: s(th.perm_city), st: s(th.perm_state), zip: s(th.perm_zip) };
  const ops = [
    t(0, 182, 182.5, m.legalName, 300, 11),
    t(0, 141, 234, perm.a1 || s(th.address), 380, 11), t(0, 141, 246.7, perm.a2, 380, 11),
    t(0, 151, 259.3, cityStZip(perm.city, perm.st, perm.zip), 370, 11),
    t(0, 188, 296.5, th.facility, 300, 11), t(0, 147, 310.4, mdy(th.start_date), 200, 11), t(0, 144, 323, mdy(th.end_date), 200, 11),
    ...yn(th.form_q_vicinity, 367.3), ...yn(th.form_q_expenses, 430.5), ...yn(th.form_q_return, 474.8), ...yn(th.form_q_foreign_student, 525.4),
  ];
  // §5: a long name may run past the underline (it ends at 319) up to just before "Date" (380.8) rather than shrink
  ops.push(...signedOps(m, 'tax_home', { x: 146, y: 601, w: 230, dateX: 410, recX: 90, recY: 628 }));
  return ops;
}

export function w4Ops(m) {
  const w = m.w4;
  const ad = m.personal;
  const ops = [
    t(0, 100, 103, [m.name.first, m.name.middle.slice(0, 1)].filter(Boolean).join(' '), 172, 10), t(0, 280, 103, m.name.last, 192, 10),
    t(0, 100, 127, [ad.address_line1, ad.address_line2].filter(Boolean).join(' '), 370, 10), t(0, 100, 151, cityStZip(ad.city, ad.state, ad.zip), 370, 10),
    t(0, 482, 103, ssnFmt(m.ssn), 90, 10),
  ];
  const fs = { single: 162, married_jointly: 174, head_of_household: 185.7 }[s(w.filing_status)];
  if (fs) ops.push({ p: 0, x: 119.2, y: fs, mark: true });
  if (yes(w.multiple_jobs)) ops.push({ p: 0, x: 568, y: 408, mark: true });
  // Step 3 in dollars exactly as the form asks (Stage 2); never computed here from counts
  ops.push(t(0, 420, 489, usd(w.step3_children_amount), 52, 10), t(0, 420, 501.5, usd(w.step3_other_amount), 52, 10),
    t(0, 514, 525.5, usd(step3Total(w)), 58, 10));
  ops.push(t(0, 514, 561.5, usd(w.other_income), 58, 10), t(0, 514, 597.5, usd(w.deductions), 58, 10), t(0, 514, 615.5, usd(w.extra_withholding), 58, 10));
  if (yes(w.exempt)) ops.push({ p: 0, x: 568, y: 658.5, mark: true });
  ops.push(...signedOps(m, 'w4', { x: 104, y: 699, w: 350, dateX: 486, recY: 685, recW: 280 }));
  return ops;
}

/** W-4 Step 3's line 3: "Add the amounts from Steps 3(a) and 3(b), plus the amount for other credits" -- the form's
 *  own sum of what the candidate entered; '' when none was entered. */
export function step3Total(w) {
  const parts = [w.step3_children_amount, w.step3_other_amount, w.step3_other_credits].map((v) => s(v)).filter(Boolean);
  if (!parts.length) return '';
  const nums = parts.map((v) => Number(v.replace(/[$,\s]/g, '')));
  return nums.every(Number.isFinite) ? String(nums.reduce((a, b) => a + b, 0)) : '';
}

export const OPS = { background: backgroundOps, direct_deposit: directDepositOps, emergency: emergencyOps, i9: i9Ops, tax_home: taxHomeOps, w4: w4Ops };

/** Portal answers that have no place on the form itself, listed on one added page (never on the form's own pages). */
export function recordLines(key, m) {
  const out = [];
  const add = (label, v) => { if (s(v)) out.push([label, s(v)]); };
  if (key === 'background') {
    // PORTAL-DEGREE-2026-10-12: the degree details for the education verification, all six, as the candidate gave them
    const b = m.background;
    if (!s(b.degree)) return out;
    const g = s(b.degree_graduated);
    add('Degree obtained', b.degree);
    add('Major or field of study', b.degree_major);
    add('Month and year graduated', /^\d{4}-\d{2}$/.test(g) ? `${g.slice(5)}/${g.slice(0, 4)}` : g);
    add('Name of school', b.degree_school);
    add('City and state of school', b.degree_school_city_state);
    add('Last name used at time of graduation', s(b.degree_last_name) || 'Same as the name above');
    return out;
  }
  const at = m.attest[key];
  if (key === 'tax_home') {
    const th = m.taxHome;
    add('Tax home address (as entered)', th.address);
    add('How long lived there', th.tenure);
    add('Monthly cost of keeping the tax home (USD)', usd(th.monthly_expense));
    for (const [n, q] of [[1, 'Q1 work part of the year near the permanent home and stay there'], [2, 'Q2 keep paying the permanent home’s living costs'],
      [3, 'Q3 permanent home where historically lived'], [4, 'Q4 family member lives there or often stays there'], [5, 'Q5 assignment expected to be temporary']]) {
      add(`IRS Publication 463 ${q}`, th[`q${n}`] === true ? 'Yes' : th[`q${n}`] === false ? 'No' : s(th[`q${n}`]));
    }
    add('Certified true and complete', th.certified === true || th.certified === 'true' ? 'Yes' : '');
  }
  if (key === 'w4') {
    add('Qualifying children (count, as entered before Step 3 was asked in dollars)', m.w4.dependents_under_17);
    add('Other dependents (count, as entered before Step 3 was asked in dollars)', m.w4.other_dependents);
    add('Amount for other credits (included in line 3)', usd(m.w4.step3_other_credits));
  }
  if (key === 'emergency') {
    for (const n of [1, 2]) add(`Contact ${n} relationship`, m.emergency[`c${n}_relationship`]);
    // Every phone the form has no slot for (it prints a home and a work phone; a mobile or other phone takes the slot
    // only when that one is empty) -- 2026-10-10: candidate 76's Contact 1 mobile was in neither place.
    const e = m.emergency;
    const ad = m.personal;
    if (s(ad.home_phone)) add('Candidate mobile phone', ad.mobile);
    for (const n of [1, 2]) {
      if (s(e[`c${n}_home_phone`])) add(`Contact ${n} mobile phone`, e[`c${n}_mobile`]);
      if (s(e[`c${n}_work_phone`])) add(`Contact ${n} other phone`, e[`c${n}_alt_phone`]);
    }
  }
  if (at && at.at) add('Page confirmed in the portal ("I attest that the information on this page is true and complete")', `${utc(at.at)}${at.ip ? ` from IP ${at.ip}` : ''}`);
  return out;
}

// ---------------------------------------------------------------- rendering (pdf-lib passed in)
function fit(font, text, size, maxW) {
  let sz = size;
  while (maxW && sz > 5 && font.widthOfTextAtSize(text, sz) > maxW) sz -= 0.25;
  return sz;
}
// Helvetica covers WinAnsi only, and the script font has no glyph for some letters. NIGHT-DIRECTSTAFF-2026-10-15 §3: a
// letter the font can't print is spelled plainly -- its accents dropped (ễ -> e, ő -> o, ș -> s) or its plain letter (Ł -> L,
// Đ -> D) -- and only a character with no plain spelling becomes "?", rather than failing the whole packet. A legal name
// prints as "Nguyen", not "Nguy?n". (The stored, signed and hashed text is never changed: this is the drawing only.)
const PLAIN = { 'Ł': 'L', 'ł': 'l', 'Đ': 'D', 'đ': 'd', 'Ħ': 'H', 'ħ': 'h', 'ı': 'i', 'Ŀ': 'L', 'ŀ': 'l', 'Ŧ': 'T', 'ŧ': 't' };
function canPrint(font, ch) {
  const fk = font && font.embedder && font.embedder.font;                   // a custom (fontkit) font: does it have the glyph?
  if (fk && typeof fk.hasGlyphForCodePoint === 'function') return fk.hasGlyphForCodePoint(ch.codePointAt(0));
  try { font.encodeText(ch); return true; } catch { return false; }        // a standard font: WinAnsi or not
}
export function winAnsiText(font, text) {
  return Array.from(String(text ?? '')).map((ch) => {
    if (!ch.trim() || canPrint(font, ch)) return ch;
    const base = ch.normalize('NFD').replace(/[̀-ͯ]/g, '');
    if (base && base !== ch && Array.from(base).every((c) => canPrint(font, c))) return base;
    const plain = PLAIN[ch];
    if (plain && canPrint(font, plain)) return plain;
    return '?';
  }).join('');
}
const winAnsi = winAnsiText;

/** script: { fontkit, bytes } -- Dancing Script, embedded as a subset; absent -> signatures in Helvetica Oblique. */
export async function fillForm(PDFLib, formBytes, ops, script = null) {
  const { PDFDocument, StandardFonts, rgb } = PDFLib;
  const doc = await PDFDocument.load(formBytes, { ignoreEncryption: true });
  const fonts = { regular: await doc.embedFont(StandardFonts.Helvetica), oblique: await doc.embedFont(StandardFonts.HelveticaOblique) };
  if (script && script.fontkit && script.bytes && ops.some((o) => o.font === 'script' && o.text)) {
    doc.registerFontkit(script.fontkit);
    fonts.script = await doc.embedFont(script.bytes, { subset: true });
  } else {
    fonts.script = fonts.oblique;
  }
  const pages = doc.getPages();
  const ink = rgb(0.05, 0.1, 0.35);
  for (const op of ops) {
    const page = pages[op.p];
    if (!page) continue;
    const H = page.getHeight();
    if (op.white) page.drawRectangle({ x: op.x, y: H - op.y - op.h, width: op.w, height: op.h, color: rgb(1, 1, 1) });
    else if (op.mark) page.drawText('X', { x: op.x - 3.2, y: H - op.y - 3.4, size: 10, font: fonts.regular, color: ink });
    else if (op.circle) page.drawEllipse({ x: op.cx, y: H - op.cy, xScale: op.rx, yScale: op.ry, borderColor: ink, borderWidth: 1 });
    else if (op.text) {
      const font = fonts[op.font || 'regular'];
      const text = winAnsi(font, op.text);
      page.drawText(text, { x: op.x, y: H - op.y, size: fit(font, text, op.size, op.maxW), font, color: ink });
    }
  }
  return doc;
}

async function addRecordPage(PDFLib, doc, title, lines, { heading = `${title}: portal record`,
  intro = 'Answers the candidate gave in the onboarding portal that have no place on the form itself. Not part of the form.' } = {}) {
  if (!lines.length) return;
  const { StandardFonts, rgb } = PDFLib;
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const page = doc.addPage([612, 792]);
  let y = 740;
  page.drawText(winAnsi(bold, heading), { x: 54, y, size: 13, font: bold });
  y -= 16;
  page.drawText(winAnsi(font, intro), { x: 54, y, size: 8.5, font, color: rgb(0.3, 0.3, 0.3) });
  y -= 24;
  for (const [label, value] of lines) {
    page.drawText(winAnsi(bold, label), { x: 54, y, size: 9, font: bold });
    y -= 12;
    page.drawText(winAnsi(font, value), { x: 66, y, size: fit(font, winAnsi(font, value), 10, 490), font });
    y -= 18;
  }
}

// ---------------------------------------------------------------- the signature certificate (PORTAL-ESIGN-PROOF-2026-10-12)
// The last page of the packet, the way a DocuSign Certificate of Completion closes an envelope: the link, the one-time
// consent to electronic records, and for every signature its form, typed name, time (UTC and Eastern), IP, browser, the
// SHA-256 of the form's content stored at signing -- and, re-hashed at download, whether that content is unchanged.
export const NOT_HASHED = 'not hashed (signed before 10-12)';
export const CERT_FORMS = { background: 'Background Consent (ESS authorization)', direct_deposit: 'Direct Deposit',
  emergency: 'Emergency Contact', tax_home: 'Tax Home Questionnaire', w4: 'Form W-4', i9: 'Form I-9, Section 1' };

export function maskEmail(v) {
  const m = String(v || '').trim().match(/^([^@\s])[^@\s]*@([^@\s])[^@\s]*?(\.[A-Za-z]{2,})$/);
  return m ? `${m[1]}***@${m[2]}***${m[3]}` : '';
}
export function maskPhone(v) {
  const d = String(v || '').replace(/\D/g, '');
  return d.length >= 4 ? `***-***-${d.slice(-4)}` : '';
}
export function etText(at) {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZoneName: 'short' }).formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute} ${p.timeZoneName}`;
}
/** "Chrome 128 on Android" from a user-agent string (the portal's functions/_lib/esign.js says the same). */
export function browserOf(ua) {
  const u = String(ua || '');
  if (!u) return '';
  const ver = (re) => { const m = u.match(re); return m ? m[1].split('.')[0] : ''; };
  const name = /Edg\//.test(u) ? `Edge ${ver(/Edg\/([\d.]+)/)}` : /OPR\//.test(u) ? `Opera ${ver(/OPR\/([\d.]+)/)}`
    : /Firefox\//.test(u) ? `Firefox ${ver(/Firefox\/([\d.]+)/)}` : /Chrome\//.test(u) ? `Chrome ${ver(/Chrome\/([\d.]+)/)}`
      : /Safari\//.test(u) && /Version\//.test(u) ? `Safari ${ver(/Version\/([\d.]+)/)}` : 'Browser';
  const os = /Android/.test(u) ? 'Android' : /iPhone|iPad|iPod/.test(u) ? 'iOS' : /Windows NT/.test(u) ? 'Windows'
    : /Mac OS X/.test(u) ? 'macOS' : /CrOS/.test(u) ? 'ChromeOS' : /Linux/.test(u) ? 'Linux' : '';
  return os ? `${name.trim()} on ${os}` : name.trim();
}
/** The tamper check: the hash stored at signing against the one taken now. */
export function verdict(stored, current) {
  if (!s(stored)) return NOT_HASHED;
  return stored === current ? 'unchanged since signing' : 'CHANGED since signing -- the stored content no longer matches';
}

/** An exam certificate's tamper check: stored at issue vs. the stored file re-hashed now. */
export function examVerdict(stored, current) {
  if (!s(stored)) return 'no certificate issued';
  if (!s(current)) return 'the stored certificate could not be read at download';
  return stored === current ? 'unchanged since issue' : 'CHANGED since issue -- the stored certificate no longer matches';
}

/** The certificate as text blocks: { heading, rows: [[label, value]] }, in order; [] when there is no certificate. */
export function certificateBlocks(m) {
  const c = m.certificate;
  if (!c) return [];
  const cand = m.candidate || {};
  const contact = [maskEmail(cand.email) && `email ${maskEmail(cand.email)}`, maskPhone(cand.phone) && `phone ${maskPhone(cand.phone)}`]
    .filter(Boolean).join(' · ') || 'none on record';
  const when = (at) => (s(at) ? `${utc(at)} (${etText(at)})` : '');
  const e = c.esign || {};
  const out = [{ heading: 'Envelope', rows: [
    ['Candidate', `${s(m.legalName) || s(cand.name) || ''} (record ${cand.id ?? ''})`.trim()],
    ['Onboarding session', String(c.session_id)],
    ['Link issued', `${when(c.session_created_at)}${s(c.session_created_by) ? ` by ${c.session_created_by}` : ''}`],
    ['Link delivered to', `the candidate's contact on record: ${contact} (staff send the link; the sending itself is not recorded)`],
    ['Electronic records consent', s(e.at)
      ? `agreed ${when(e.at)}${s(e.ip) ? ` · IP ${e.ip}` : ''}${browserOf(e.user_agent) ? ` · ${browserOf(e.user_agent)}` : ''}`
      : 'not recorded (this link began before the consent screen, 10-12)'],
    ['Completed', c.completed_at ? when(c.completed_at) : 'not yet'],
  ] }];
  const sigs = [...(c.forms || []).map((f) => ({ ...f, key: f.form })), ...(c.background ? [{ ...c.background, key: 'background' }] : [])];
  const order = ['background', 'emergency', 'tax_home', 'w4', 'i9', 'direct_deposit'];
  sigs.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  for (const g of sigs) {
    out.push({ heading: CERT_FORMS[g.key] || g.key, rows: [
      ['Typed signature', s(g.signature)],
      ['Signed', when(g.signed_at)],
      ['Consent box', g.consented ? 'checked' : 'not checked'],
      ['IP address', s(g.signed_ip) || 'not recorded'],
      ['Browser', browserOf(g.user_agent) || 'not recorded'],
      ['Content SHA-256', s(g.stored_sha256) || NOT_HASHED],
      ['Tamper check at download', verdict(g.stored_sha256, g.current_sha256)],
    ] });
  }
  // ONBOARD-EXAMS-v2-2026-10-16: passed exams, the same way: the certificate's SHA-256 stored at issue, re-hashed now
  for (const x of c.exams || []) {
    out.push({ heading: `${(EXAM_BY_ID[x.exam_id] && EXAM_BY_ID[x.exam_id].title) || x.exam_id} (exam; not part of the packet)`, rows: [
      ['Name on certificate', s(x.cert_name)],
      ['Result', `passed ${Number(x.score_pct)}% (${x.correct} of ${x.total}), attempt ${x.attempt_no}`],
      ['Submitted', when(x.submitted_at)],
      ['IP address', s(x.submit_ip) || 'not recorded'],
      ['Browser', browserOf(x.submit_user_agent) || 'not recorded'],
      ['Certificate SHA-256', s(x.cert_sha256) || 'no certificate issued'],
      ['Tamper check at download', examVerdict(x.cert_sha256, x.current_sha256)],
    ] });
  }
  return out;
}

async function addCertificatePage(PDFLib, doc, model, now = new Date()) {
  const blocks = certificateBlocks(model);
  if (!blocks.length) return;
  const { StandardFonts, rgb } = PDFLib;
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  let page = doc.addPage([612, 792]);
  let y = 740;
  const room = (h) => {
    if (y - h >= 54) return;
    page = doc.addPage([612, 792]);
    page.drawText('Signature certificate (continued)', { x: 54, y: 740, size: 11, font: bold });
    y = 716;
  };
  // long values wrap (never shrink to unreadable): lines of at most `width` points
  const wrap = (f, text, size, width) => {
    const out = [];
    let line = '';
    for (const word of winAnsi(f, text).split(/\s+/).filter(Boolean)) {
      const next = line ? `${line} ${word}` : word;
      if (f.widthOfTextAtSize(next, size) > width && line) { out.push(line); line = word; } else line = next;
    }
    out.push(line);
    return out;
  };
  page.drawText('Signature certificate', { x: 54, y, size: 15, font: bold });
  y -= 16;
  for (const line of wrap(font, `Built ${utc(now.toISOString())} from the onboarding portal's records, when this packet was downloaded. `
    + "Each form's content hash was taken when it was signed and re-taken now.", 8, 504)) {
    page.drawText(line, { x: 54, y, size: 8, font, color: rgb(0.3, 0.3, 0.3) });
    y -= 10;
  }
  y -= 12;
  for (const b of blocks) {
    room(16 + (b.rows.length + 1) * 12);
    page.drawText(winAnsi(bold, b.heading), { x: 54, y, size: 11, font: bold });
    y -= 14;
    for (const [label, value] of b.rows) {
      const changed = label === 'Tamper check at download' && /^CHANGED/.test(value);
      const f = changed ? bold : font;
      const lines = wrap(f, value, 8.5, 362);
      room(12 * lines.length);
      page.drawText(winAnsi(font, label), { x: 66, y, size: 8.5, font, color: rgb(0.35, 0.38, 0.45) });
      for (const line of lines) {
        page.drawText(line, { x: 196, y, size: 8.5, font: f, color: changed ? rgb(0.73, 0.11, 0.11) : rgb(0.06, 0.09, 0.16) });
        y -= 12;
      }
    }
    y -= 8;
  }
  room(30);
  for (const line of ['Content hash: SHA-256 of the form\'s stored answers, About you, its attachments and the typed signature (for the background',
    'authorization: its own record). The SSN and bank numbers stay encrypted and are not part of it.']) {
    page.drawText(winAnsi(font, line), { x: 54, y, size: 7.5, font, color: rgb(0.35, 0.38, 0.45) });
    y -= 10;
  }
}

/** One filled form as a PDFDocument: Matt's pages with the overlay, then (when there is one) its portal-record page. */
export async function renderForm(PDFLib, key, formBytes, model, script = null) {
  const doc = await fillForm(PDFLib, formBytes, OPS[key](model), script);
  if (key !== 'background') await addRecordPage(PDFLib, doc, FORMS.find((f) => f.key === key).label, recordLines(key, model));
  // PORTAL-DEGREE-2026-10-12: right after the signed consent, the degree details for the education verification
  else await addRecordPage(PDFLib, doc, '', recordLines('background', model), { heading: 'Education verification: degree details',
    intro: 'As the candidate gave them in the onboarding portal when signing the background authorization above.' });
  return doc;
}

const IMAGE = { 'image/jpeg': 'embedJpg', 'image/png': 'embedPng' };
export function sniff(bytes) {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if (b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46) return 'application/pdf';
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'image/jpeg';
  if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return 'image/png';
  return null;
}

/**
 * The packet: the six filled forms in order, then the uploads (voided check, ID photos, I-20 if any).
 * fetchForm(file) -> bytes;  uploads: [{ kind, label, bytes }] already fetched through 60-second links.
 * -> { bytes, pages, skipped: [kind] } (an upload pdf-lib cannot embed -- an older HEIC or WebP -- is listed, not dropped silently)
 */
export async function buildPacket(PDFLib, { fetchForm, model, uploads = [], only = null, script = null }) {
  const { PDFDocument, StandardFonts } = PDFLib;
  const out = await PDFDocument.create();
  out.setTitle(only ? `${FORMS.find((f) => f.key === only).label} (filled)` : 'Onboarding packet');
  out.setProducer('TherapyLink CRM');
  const skipped = [];
  const font = await out.embedFont(StandardFonts.Helvetica);
  const embed = async (u, heading) => {
    const type = sniff(u.bytes);
    if (type === 'application/pdf') {
      const src = await PDFDocument.load(u.bytes, { ignoreEncryption: true });
      for (const pg of await out.copyPages(src, src.getPageIndices())) out.addPage(pg);
    } else if (IMAGE[type]) {
      const img = await out[IMAGE[type]](u.bytes);
      const page = out.addPage([612, 792]);
      page.drawText(winAnsi(font, heading ? `${heading} -- ${u.label}` : u.label), { x: 36, y: 760, size: 11, font });
      const scale = Math.min(540 / img.width, 690 / img.height, 1);
      page.drawImage(img, { x: 36, y: 740 - img.height * scale, width: img.width * scale, height: img.height * scale });
    } else {
      skipped.push(u.kind);
    }
  };
  for (const f of FORMS.filter((x) => !only || x.key === only)) {
    const filled = await renderForm(PDFLib, f.key, await fetchForm(f.file), model, script);
    for (const pg of await out.copyPages(filled, filled.getPageIndices())) out.addPage(pg);
    if (f.key === 'i9') {
      for (const u of uploads.filter((x) => BESIDE_I9.includes(x.kind))) await embed(u, 'For Form I-9 Section 2');
    }
  }
  if (!only) {
    for (const u of uploads.filter((x) => !BESIDE_I9.includes(x.kind))) await embed(u);
    await addCertificatePage(PDFLib, out, model);          // PORTAL-ESIGN-PROOF-2026-10-12: the last page
  }
  return { bytes: await out.save(), pages: out.getPageCount(), skipped };
}
